// Image-generation client. ImagePage fires every provider in parallel
// via Promise.allSettled so one slow/failing provider can't block the
// others. Each backend route returns { provider, imageDataUrl, latencyMs,
// usage? } — the usage block is the post-call counter snapshot the UI
// uses to bump the meter.

import { getToken } from './auth';
import { getAnonUserId } from './localChats';

const API_BASE = (process.env.REACT_APP_API_BASE || '').replace(/\/$/, '');

function ownerHeaders() {
  const token = getToken();
  if (token) return { Authorization: `Bearer ${token}` };
  const uid = getAnonUserId();
  return uid ? { 'X-Anon-Uid': uid } : {};
}

export async function generateImage({ provider, prompt, dim, signal } = {}) {
  const res = await fetch(`${API_BASE}/api/image/${provider}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...ownerHeaders() },
    // `dim` is something like '1:1' / '16:9' / '2:3' / '3:4' / '9:16'.
    // The backend converts it to width/height or aspect_ratio depending
    // on the provider (Pollinations → w/h params, Stability → aspect_ratio
    // field, others ignore it). Missing → provider's default square.
    body: JSON.stringify({ prompt, dim }),
    signal,
  });
  let data;
  try {
    data = await res.json();
  } catch {
    throw new Error(`Non-JSON response (HTTP ${res.status}).`);
  }
  if (!res.ok) {
    const err = new Error(data?.error?.message || `Request failed (HTTP ${res.status}).`);
    err.status = res.status;
    err.code = data?.error?.code;
    err.kind = data?.error?.kind;
    err.usage = data?.usage || null;
    throw err;
  }
  return data;
}

export async function enhancePrompt({ prompt, enhancer, signal } = {}) {
  const res = await fetch(`${API_BASE}/api/image/enhance`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ prompt, enhancer }),
    signal,
  });
  let data;
  try {
    data = await res.json();
  } catch {
    throw new Error(`Non-JSON response (HTTP ${res.status}).`);
  }
  if (!res.ok) {
    throw new Error(data?.error?.message || `Request failed (HTTP ${res.status}).`);
  }
  return data;
}

// Grouped by section in the UI — must match the backend provider keys.
export const PROVIDER_SECTIONS = [
  {
    title: 'External APIs',
    providers: [
      { id: 'hf', label: 'Hugging Face', model: 'FLUX.1-schnell' },
      { id: 'pollinations', label: 'Pollinations.ai', model: 'Public' },
    ],
  },
  {
    title: 'AWS Bedrock · Stability AI',
    subtitle: 'us-west-2',
    providers: [
      { id: 'stable-core', label: 'Stable Core', model: 'v1.1' },
      { id: 'stable-ultra', label: 'Stable Ultra', model: 'v1.1' },
      { id: 'sd35', label: 'SD3.5 Large', model: 'v1' },
    ],
  },
];

export const ALL_PROVIDERS = PROVIDER_SECTIONS.flatMap((s) => s.providers);
